
import { Chart, registerables } from 'chart.js'
import { LineChart, useLineChart } from 'vue-chart-3'
import { computed, defineComponent, PropType } from 'vue'
import { DOWN_TEETH, UP_TEETH } from '@/shared/constants'
import { PeriodontalDiagramType, TeethType } from '@/shared/types'
import { useRoute } from 'vue-router'

Chart.register(...registerables)

export default defineComponent({
  name: 'PeriodontalChart',
  components: {
    LineChart,
  },

  props: {
    data: Object as PropType<PeriodontalDiagramType>,
    isReverse: Boolean,
    isUpperTeeth: Boolean,
    isPdfMode: Boolean,
    isInteractive: Boolean,
  },

  setup(props) {
    const route = useRoute()

    const isPreviewMode = computed(() => route.query['view-mode'] === '1')
    const teethList = computed(() => {
      if (props.isUpperTeeth) {
        return props.isReverse ? DOWN_TEETH : UP_TEETH
      }

      return props.isReverse ? UP_TEETH : DOWN_TEETH
    })

    const recession = computed(() => {
      const items = props.data?.recession.map((item) => {
        if (item < -10) return 10

        if (item > 16) return -16

        return -item
      })

      if (!props.isReverse) {
        return items?.map((item) => item * -1)
      }

      return items
    })

    const sounding = computed(() => {
      const computeSounding = props.data?.sounding?.map(
        (soundingValue, index) => {
          const recessionValue = props.data?.recession[index]
          const sumOfValues = soundingValue + recessionValue
          return sumOfValues > 20 ? 20 : sumOfValues
        }
      )
      if (props.isReverse) {
        return computeSounding?.map((item) => item * -1)
      }

      return computeSounding
    })

    const labels = computed(() => props.data?.recession)

    const chartData = computed(() => ({
      labels: labels.value,
      datasets: [
        {
          data: recession.value,
          borderColor: '#fa8a8a',
          pointRadius: 0,
          spanGaps: true,
          borderWidth: 1,
        },
        {
          data: sounding.value,
          fill: '-1',
          borderColor: '#696dce',
          pointRadius: 0,
          backgroundColor: 'rgba(105, 109, 206, 0.7)',
          borderWidth: 3,
        },
      ],
    }))

    const displayYAxes = (currentChart) => {
      const context = currentChart.chart.ctx

      context.fillStyle = '#222'

      if (!isPreviewMode.value) {
        context.font = '9px Manrope'
      } else {
        context.font = '8px Manrope'
      }

      if (props.isReverse) {
        if (props.isInteractive) {
          fillLegendText(context, 43, 61, 78, 94, 114)
        } else if (isPreviewMode.value) {
          fillLegendText(context, 32, 45, 56, 67, 82)
        } else {
          fillLegendText(context, 36, 51, 63, 78, 94)
        }

        return
      }

      if (props.isInteractive) {
        fillLegendText(context, 80, 64, 46, 30, 10)
      } else if (isPreviewMode.value) {
        fillLegendText(context, 58, 46, 34, 22, 10)
      } else {
        fillLegendText(context, 66, 52, 39, 25, 10)
      }
    }

    const fillLegendText = (context: any, ...values: number[]): void => {
      context.fillText('0', 0, values[0])
      context.fillText('5', 0, values[1])
      context.fillText('10', 0, values[2])
      context.fillText('15', 0, values[3])
      context.fillText('20', 0, values[4])
    }

    const options = computed(() => ({
      events: [],
      hover: false,
      animation: {
        duration: 500,
        onComplete(currentChart) {
          displayYAxes(currentChart)
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false,
      },
      scales: {
        x: {
          grid: {
            tickColor: 'transparent',
            drawOnChartArea: false,
          },
          ticks: {
            color: 'transparent',
          },
        },
        y: {
          min: props.isReverse ? -21 : -10,
          max: props.isReverse ? 10 : 21,
          ticks: {
            color: 'transparent',
            font: {
              size: 5,
            },
          },
        },
      },
    }))

    const checkIsToothRemoved = (toothItemIds: number[]): boolean => {
      return (
        props.data?.removedTooth.includes(toothItemIds[0]) ||
        props.data?.removedTooth.includes(toothItemIds[1])
      )
    }

    const checkIsImplant = (toothItemIds: number[]): boolean => {
      return (
        props.data?.implantTooth.includes(toothItemIds[0]) ||
        props.data?.implantTooth.includes(toothItemIds[1])
      )
    }

    const getToothWidth = (teeth: TeethType): number => {
      if (props.isInteractive) {
        return teeth.width + 10
      }

      return teeth.width
    }

    const getToothHeight = (teeth: TeethType): number => {
      if (props.isInteractive) {
        return teeth.height + 20
      }

      return teeth.height
    }

    const { lineChartProps } = useLineChart({
      chartData,
      options: options.value,
    })

    return {
      isPreviewMode,
      teethList,
      lineChartProps,
      checkIsToothRemoved,
      checkIsImplant,
      getToothWidth,
      getToothHeight,
    }
  },
})
